package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PlanCompareOption
import com.example.positionahead.model.PlanPositionDiff
import com.example.positionahead.model.PortfolioObjective
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.Position
import com.example.positionahead.model.PositionLot
import com.example.positionahead.model.Quote
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.model.SavedPortfolio
import com.example.positionahead.model.SymbolPerformance
import com.example.positionahead.model.TargetHolding
import com.example.positionahead.model.effectiveBondTarget
import com.example.positionahead.model.effectiveStockTarget
import com.example.positionahead.model.objectiveLabel
import kotlin.math.abs
import kotlin.random.Random

object PlanStrategyBuilder {

    fun buildCompareOptions(
        currentPositions: List<Position>,
        currentSummary: PortfolioSummary,
        goal: FinancialGoal,
        targetHoldings: List<TargetHolding>,
        trades: List<RebalanceTrade>,
        discoverSections: List<com.example.positionahead.model.DiscoverSection>,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance>,
        savedPortfolios: List<SavedPortfolio>,
        activePortfolioId: String,
    ): List<PlanCompareOption> {
        val total = currentSummary.totalMarketValue
        if (total <= 0) return emptyList()

        val options = mutableListOf<PlanCompareOption>()

        fun add(id: String, title: String, subtitle: String, positions: List<Position>, why: String) {
            if (positions.isEmpty()) return
            var summary = PortfolioCalculator.buildSummary(positions, quotes)
            summary = PerformanceCalculator.applyPerformance(summary, performance)
            options += PlanCompareOption(id, title, subtitle, summary, why)
        }

        add(
            id = "strategy_goal_cap",
            title = "Your goals · capped & rebalanced",
            subtitle = "${goal.effectiveStockTarget()}% / ${goal.effectiveBondTarget()}% · max ${goal.maxSinglePositionPercent.toInt()}% per name",
            positions = buildGoalCappedPortfolio(currentSummary, goal, quotes),
            why = "Trims concentrated positions and shifts toward your goal mix using your symbols plus core bond/equity ETFs.",
        )

        if (trades.isNotEmpty()) {
            add(
                id = "strategy_trades",
                title = "Action plan · dollar trades",
                subtitle = "${trades.size} move(s) from concentration & allocation drift",
                positions = PlanSimulator.applyTrades(currentPositions, trades, quotes),
                why = "Applies the specific buy/sell amounts the planner computed from your holdings and goals.",
            )
        }

        val suggested = SuggestedPortfolioBuilder.build(
            currentPositions = currentPositions,
            summary = currentSummary,
            goal = goal,
            targetHoldings = targetHoldings,
            trades = emptyList(),
            discoverSections = discoverSections,
            quotes = quotes,
        )
        add(
            id = "strategy_goal_discover",
            title = "Your profile · goal + Discover",
            subtitle = "Aligned with ${goal.objectiveLabel()}",
            positions = suggested.positions,
            why = suggested.description,
        )

        modelTemplateFor(goal.objective, goal)?.let { template ->
            add(
                id = template.id,
                title = template.title,
                subtitle = "Model portfolio for ${goal.objectiveLabel()} investors",
                positions = positionsFromWeights(total, template.weights, quotes),
                why = template.why,
            )
        }

        add(
            id = "model_balanced",
            title = "Classic balanced",
            subtitle = "VOO · VXUS · BND",
            positions = positionsFromWeights(
                total,
                mapOf("VOO" to 50.0, "VXUS" to 20.0, "BND" to 30.0),
                quotes,
            ),
            why = "Independent benchmark: US large-cap, international, and total bond market.",
        )

        add(
            id = "model_growth",
            title = "Growth tilt",
            subtitle = "QQQM · VTI · VXUS · BND",
            positions = positionsFromWeights(
                total,
                mapOf("QQQM" to 40.0, "VTI" to 30.0, "VXUS" to 15.0, "BND" to 15.0),
                quotes,
            ),
            why = "Higher equity growth emphasis than a balanced portfolio; still includes bonds.",
        )

        add(
            id = "model_income",
            title = "Income focus",
            subtitle = "SCHD · JEPI · BIV · VOO",
            positions = positionsFromWeights(
                total,
                mapOf("SCHD" to 30.0, "JEPI" to 25.0, "BIV" to 25.0, "VOO" to 20.0),
                quotes,
            ),
            why = "Dividend and premium-income ETFs plus investment-grade bonds.",
        )

        savedPortfolios
            .filter { it.id != activePortfolioId && it.positions.isNotEmpty() }
            .forEach { saved ->
                add(
                    id = "saved_${saved.id}",
                    title = saved.name,
                    subtitle = if (saved.isTheoretical) "Your saved theoretical portfolio" else "Saved account",
                    positions = saved.positions,
                    why = "Compare against another portfolio you saved in the app.",
                )
            }

        val distinct = options.distinctBy { it.id }
        val filtered = distinct.filter { isMeaningfullyDifferent(it.summary, currentSummary) }
        return filtered.ifEmpty { distinct.take(4) }
    }

    fun diffVsCurrent(
        current: PortfolioSummary,
        alternative: PortfolioSummary,
        alternativeWhy: String,
    ): List<PlanPositionDiff> {
        val cur = current.positions.associateBy { it.position.symbol.uppercase() }
        val alt = alternative.positions.associateBy { it.position.symbol.uppercase() }
        val symbols = (cur.keys + alt.keys).sorted()

        return symbols.mapNotNull { symbol ->
            val c = cur[symbol]
            val a = alt[symbol]
            val cVal = c?.marketValue ?: 0.0
            val aVal = a?.marketValue ?: 0.0
            val delta = aVal - cVal
            if (c == null && a == null) return@mapNotNull null
            if (abs(delta) < 0.5 && c != null && a != null) return@mapNotNull null

            val explanation = when {
                c == null -> "New in this strategy: $alternativeWhy"
                a == null -> "Removed vs current — weight moved to other holdings in this strategy."
                delta > 0 -> "Higher weight here in this strategy (${formatPct(a.portfolioWeightPercent)} vs ${formatPct(c.portfolioWeightPercent)})."
                else -> "Lower weight here (${formatPct(a.portfolioWeightPercent)} vs ${formatPct(c.portfolioWeightPercent)})."
            }
            PlanPositionDiff(
                symbol = symbol,
                currentMarketValue = cVal,
                alternativeMarketValue = aVal,
                deltaDollars = delta,
                currentWeightPercent = c?.portfolioWeightPercent ?: 0.0,
                alternativeWeightPercent = a?.portfolioWeightPercent ?: 0.0,
                explanation = explanation,
            )
        }.sortedByDescending { abs(it.deltaDollars) }
    }

    private fun isMeaningfullyDifferent(a: PortfolioSummary, b: PortfolioSummary): Boolean {
        if (abs(a.totalMarketValue - b.totalMarketValue) > 1.0) return true
        val aMap = a.positions.associate { it.position.symbol.uppercase() to it.portfolioWeightPercent }
        val bMap = b.positions.associate { it.position.symbol.uppercase() to it.portfolioWeightPercent }
        val symbols = aMap.keys + bMap.keys
        return symbols.any { sym ->
            abs((aMap[sym] ?: 0.0) - (bMap[sym] ?: 0.0)) > 0.75
        } || aMap.keys != bMap.keys
    }

    private fun buildGoalCappedPortfolio(
        current: PortfolioSummary,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
    ): List<Position> {
        val total = current.totalMarketValue
        val maxDollars = total * goal.maxSinglePositionPercent / 100.0
        val mutable = current.positions.associate { it.position.symbol.uppercase() to it.marketValue }.toMutableMap()
        var pool = 0.0
        mutable.keys.toList().forEach { sym ->
            val v = mutable[sym] ?: 0.0
            if (v > maxDollars) {
                pool += v - maxDollars
                mutable[sym] = maxDollars
            }
        }

        val stockTarget = total * goal.effectiveStockTarget() / 100.0
        val bondTarget = total * goal.effectiveBondTarget() / 100.0
        val equitySymbols = current.positions.filter {
            it.position.assetClass != AssetClass.BOND
        }.map { it.position.symbol.uppercase() }.toSet()
        val bondSymbols = current.positions.filter {
            it.position.assetClass == AssetClass.BOND
        }.map { it.position.symbol.uppercase() }.toSet()

        var stockNow = mutable.filterKeys { it in equitySymbols }.values.sum()
        var bondNow = mutable.filterKeys { it in bondSymbols }.values.sum()

        val bondFill = listOf("BND", "BIV", "AGG").firstOrNull { quotes[it]?.price ?: 0.0 > 0 } ?: "BND"
        val equityFill = when (goal.objective) {
            PortfolioObjective.GROWTH -> listOf("QQQM", "VTI", "VOO")
            PortfolioObjective.INCOME -> listOf("SCHD", "JEPI", "VOO")
            PortfolioObjective.CAPITAL_PRESERVATION -> listOf("VOO", "VTI")
            PortfolioObjective.BALANCED -> listOf("VOO", "VXUS", "VTI")
        }.firstOrNull { quotes[it]?.price ?: 0.0 > 0 } ?: "VOO"

        if (stockNow < stockTarget - 1) {
            val add = (stockTarget - stockNow).coerceAtMost(pool)
            pool -= add
            mutable[equityFill] = (mutable[equityFill] ?: 0.0) + add
            stockNow += add
        }
        if (bondNow < bondTarget - 1) {
            val add = (bondTarget - bondNow).coerceAtMost(pool)
            pool -= add
            mutable[bondFill] = (mutable[bondFill] ?: 0.0) + add
        }
        if (pool > 1.0) {
            mutable[bondFill] = (mutable[bondFill] ?: 0.0) + pool * 0.5
            mutable[equityFill] = (mutable[equityFill] ?: 0.0) + pool * 0.5
        }

        return mutable.mapNotNull { (symbol, dollars) ->
            if (dollars <= 0.5) return@mapNotNull null
            val price = quotes[symbol]?.price?.takeIf { it > 0 } ?: return@mapNotNull null
            lotPosition(symbol, dollars / price, price, "Goal cap & mix")
        }
    }

    private data class ModelTemplate(
        val id: String,
        val title: String,
        val weights: Map<String, Double>,
        val why: String,
    )

    private fun modelTemplateFor(
        objective: PortfolioObjective,
        goal: FinancialGoal,
    ): ModelTemplate? {
        val stock = goal.effectiveStockTarget().toDouble()
        val bond = goal.effectiveBondTarget().toDouble()
        return when (objective) {
            PortfolioObjective.GROWTH -> ModelTemplate(
                id = "model_profile_growth",
                title = "Profile match · growth",
                weights = mapOf("QQQM" to stock * 0.45, "VTI" to stock * 0.35, "VXUS" to stock * 0.20, "BND" to bond),
                why = "Uses your age/risk stock % with a growth-oriented equity split.",
            )
            PortfolioObjective.INCOME -> ModelTemplate(
                id = "model_profile_income",
                title = "Profile match · income",
                weights = mapOf("SCHD" to stock * 0.35, "JEPI" to stock * 0.25, "VOO" to stock * 0.40, "BIV" to bond),
                why = "Uses your stock/bond targets with dividend and bond income emphasis.",
            )
            PortfolioObjective.CAPITAL_PRESERVATION -> ModelTemplate(
                id = "model_profile_preservation",
                title = "Profile match · preservation",
                weights = mapOf("BND" to bond * 0.55, "BIV" to bond * 0.45, "VOO" to stock),
                why = "Heavy investment-grade bonds with a smaller equity core.",
            )
            PortfolioObjective.BALANCED -> ModelTemplate(
                id = "model_profile_balanced",
                title = "Profile match · balanced",
                weights = mapOf("VOO" to stock * 0.55, "VXUS" to stock * 0.45, "BND" to bond),
                why = "Classic balanced split sized to your personal stock/bond targets.",
            )
        }
    }

    private fun positionsFromWeights(
        totalValue: Double,
        weights: Map<String, Double>,
        quotes: Map<String, Quote>,
    ): List<Position> {
        val normalized = weights.entries.filter { it.value > 0 }
        val sum = normalized.sumOf { it.value }
        if (sum <= 0) return emptyList()
        return normalized.mapNotNull { (symbol, weight) ->
            val dollars = totalValue * weight / sum
            val price = quotes[symbol.uppercase()]?.price?.takeIf { it > 0 } ?: return@mapNotNull null
            lotPosition(symbol.uppercase(), dollars / price, price, "Model ${weight.format(0)}%")
        }
    }

    private fun lotPosition(symbol: String, shares: Double, price: Double, note: String): Position {
        val asset = when (symbol) {
            "BND", "AGG", "TLT", "BIV", "VCIT" -> AssetClass.BOND
            else -> AssetClass.ETF
        }
        return Position(
            id = Random.nextLong().toString(16),
            symbol = symbol,
            assetClass = asset,
            lots = listOf(
                PositionLot(
                    id = Random.nextLong().toString(16),
                    shares = shares,
                    costBasisPerShare = price,
                    purchaseDate = "Strategy",
                    note = note,
                ),
            ),
        )
    }

    private fun formatPct(v: Double) = "${v.format(1)}%"

    private fun Double.format(decimals: Int): String {
        val factor = 10.0.pow(decimals)
        return (kotlin.math.round(this * factor) / factor).toString()
    }

    private fun Double.pow(n: Int): Double {
        var result = 1.0
        repeat(n) { result *= 10.0 }
        return result
    }
}
